import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";

import { useGetConfigurationList } from "@/api/configuration/useConfigurationAPI";
import {
  useAddRowsDataset,
  useDeleteRowsDataset,
  useMutationDeleteDataset,
  useMutationPatchDatasetMetadata,
  useUpdateRowsDataset
} from "@/api/dataset/useDatasetAPI";
import appFeatures from "@/appFeatures";
import { ArrowLeftIcon, ArrowRightIcon, LoaderIcon } from "@/assets/icons";
import { ActionPanel } from "@/components/datasetManagement/details/ActionPanel";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import ConfirmationDialog from "@/components/datasetManagement/details/ConfirmationDialog";
import ConfirmationNullDialog from "@/components/datasetManagement/details/ConfirmationNullDialog";
import DatasetConfigurationSection from "@/components/datasetManagement/details/DatasetConfigurationSection";
import DataSettingPopup from "@/components/datasetManagement/details/DataSettingPopup";
import DataSettingsDialog from "@/components/datasetManagement/details/dataSettingsV2";
import TableContainer from "@/components/datasetManagement/details/TableContainer";
import LoadingState from "@/components/datasetManagement/LoadingState";
import UploadDialog, {
  EUploadDatasetType
} from "@/components/datasetManagement/UploadDialog";
import NotificationContent from "@/components/notification/notificationContent";
import {
  BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID,
  BRING_YOUR_OWN_DATA_MOCKS,
  BRING_YOUR_OWN_DATA_PUBLISH_DATA_TOUR_ID,
  BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID,
  BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_TOUR_ID
} from "@/components/onboarding/configs/bring-your-own-data.mocks";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import EditField from "@/components/ui/editField";
import { ROW_ID_DETAIL_DATASET } from "@/constants/dataset";
import {
  ERROR_MESSAGE,
  MESSAGE,
  TARGETIN_SYNC_MESSAGE
} from "@/constants/message";
import { capitalizeFirst } from "@/helpers/string";
import { useConnectorPullSync } from "@/hooks/dataset-managment/useConnectorPullSync";
import { useDatasetDataSetting } from "@/hooks/dataset-managment/useDatasetDataSetting";
import {
  convertDatasetDataToTableData,
  useDetailDataset
} from "@/hooks/dataset-managment/useDetailDataset";
import { useDataset } from "@/hooks/map/useDataset";
import { showPermissionDeniedToast } from "@/hooks/permission/guardPermission";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useGuardPermission } from "@/hooks/permission/useGuardPermission";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useParsingStatus } from "@/hooks/useParsingStatus";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setDatasetHistory } from "@/store/slice/datasetSlice";
import { selectIsOnboardingActive } from "@/store/slice/onboardingSlice";
import { clearDrawPolygonDataset } from "@/store/slice/spatialSlice";
import {
  resetUIDataConfig,
  setShowPanelDatasetDetail
} from "@/store/slice/uiSlice";
import { setWidgetHistory } from "@/store/slice/widgetSlice";
import {
  DatasetAttributeSchema,
  DatasetBasicInfoForm,
  DatasetPublishStatus
} from "@/types/dataset";
import { DataSettings } from "@/types/dataSetting";
import { withBasePath } from "@/utils/basePath";
import {
  convertDatasetResponseToFeatureCollection,
  formatAddAndUpdateDatasetRow,
  isFeatureCollectionWGS84
} from "@/utils/dataset";

const TEST_ID_PREFIX = "dataset-management-detail";
// One shared empty list, so the schema prop keeps its identity while the dataset loads.
const EMPTY_SCHEMA: DatasetAttributeSchema[] = [];

/**
 * The selection the tour puts in the Data Settings popup: nothing on the popup
 * step, the mock dataset's country on the spatial-aggregation step, and Point
 * selected on the follow-up step. The live dataSettingTemp (seeded by
 * useDatasetDataSetting's effects) stays untouched behind the scenes.
 */
function getTourDataSetting({
  isSpatialAggregationStep,
  isSpatialAggregationPointStep
}: {
  isSpatialAggregationStep: boolean;
  isSpatialAggregationPointStep: boolean;
}): DataSettings | null {
  if (isSpatialAggregationPointStep) {
    return BRING_YOUR_OWN_DATA_MOCKS.dataSettingWithPoint;
  }

  if (isSpatialAggregationStep) {
    return BRING_YOUR_OWN_DATA_MOCKS.dataSettingWithCountry;
  }

  return null;
}

/**
 * What the onboarding tour dictates for the Data Settings popup on the step
 * that is running. Outside the tour none of it applies and the popup follows
 * the user-driven state.
 */
function getDataSettingPopupTourState(
  isBringYourOwnDataOnboarding: boolean,
  activeStepTourId: string | null
): {
  isPopupOpen: boolean;
  dataSetting: DataSettings | null;
  spatialAggregationTourId: string;
  geometryAttributeOptions?: IOptionItem[];
} {
  const isSpatialAggregationStep =
    isBringYourOwnDataOnboarding &&
    activeStepTourId === BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_TOUR_ID;
  const isSpatialAggregationPointStep =
    isBringYourOwnDataOnboarding &&
    activeStepTourId === BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID;

  // During the tour the popup belongs to these popup targets only. Tie its
  // visibility to them so navigating away (Prev/Next/Skip) closes it — the open
  // flag alone is one-shot and would otherwise linger on other steps.
  const isPopupOpen =
    (isBringYourOwnDataOnboarding &&
      activeStepTourId === BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID) ||
    isSpatialAggregationStep ||
    isSpatialAggregationPointStep;

  return {
    isPopupOpen,
    dataSetting: getTourDataSetting({
      isSpatialAggregationStep,
      isSpatialAggregationPointStep
    }),
    spatialAggregationTourId: isSpatialAggregationPointStep
      ? BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID
      : BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_TOUR_ID,
    geometryAttributeOptions: isSpatialAggregationPointStep
      ? BRING_YOUR_OWN_DATA_MOCKS.pointGeometryAttributeOptions
      : undefined
  };
}

export default function DatasetManagementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { isRowDatasetManagementEnabled, isGranularRbacEnabled } =
    useAppFeatures();

  const guardPermission = useGuardPermission();
  const userPermissions = useGetUserPermission();
  const isEditDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.EDIT];
  const isDeleteDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.DELETE];
  // With granular RBAC, edit actions stay visible and show a toast when blocked
  const isEditBlocked = isGranularRbacEnabled && !isEditDataGranted;

  const { handleClearDataset } = useDataset();
  const {
    dynamicColumns,
    data,
    isLoading,
    metadata,
    datasetData,
    highlightedRow,
    setHighlightedRow,
    setRefetchStream
  } = useDetailDataset();
  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const {
    widgets,
    visualizationOrder: widgetVisualizationOrder,
    widgetIndicates
  } = useSelector((state: RootState) => state.widget);

  const isBringYourOwnDataOnboarding = useSelector(selectIsOnboardingActive);
  const activeStepTourId = useSelector(
    (state: RootState) => state.onboarding.activeStepTourId
  );

  const dataSettingPopupTour = getDataSettingPopupTourState(
    isBringYourOwnDataOnboarding,
    activeStepTourId
  );

  const { showConfirmation } = useConfirmationModal();
  const [open, setOpen] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] =
    useState<EUploadDatasetType | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [dataSetting, setDataSetting] = useState<DataSettings | null>(null);
  const [showDataSettingPopup, setShowDataSettingPopup] =
    useState<boolean>(false);
  const [isNullConfirmationOpen, setIsNullConfirmationOpen] =
    useState<boolean>(false);
  const { removeSessionItem: removeFormSettingDataset } = useSessionStorage(
    "dataset-detail-draw-polygon",
    {}
  );
  const { startSync, isSyncing: isDatasetSyncing } = useConnectorPullSync({
    onSuccess: (_dataset, addedRows) => {
      setRefetchStream(true);
      const hasAddedRows = addedRows > 0;
      const title = hasAddedRows
        ? TARGETIN_SYNC_MESSAGE.DETAIL_SUCCESS_TITLE
        : TARGETIN_SYNC_MESSAGE.DETAIL_NO_NEW_ROWS_TITLE;
      const message = hasAddedRows
        ? TARGETIN_SYNC_MESSAGE.DETAIL_SUCCESS_DESCRIPTION(addedRows)
        : TARGETIN_SYNC_MESSAGE.DETAIL_NO_NEW_ROWS_DESCRIPTION;
      showToast({ type: EToastType.SUCCESS, title, message });
    },
    onError: () =>
      showToast({
        type: EToastType.ERROR,
        title: TARGETIN_SYNC_MESSAGE.FAILED_TITLE,
        message: TARGETIN_SYNC_MESSAGE.FAILED_DESCRIPTION
      }),
    // Per dataset, so another dataset's pull is never resumed on this page.
    storageKey: id ? `targetin-sync-running-detail-${id}` : undefined
  });

  /** True while this dataset is being pulled again from its connector. */
  const isSyncing = !!id && isDatasetSyncing(id);

  const handleSyncDataset = () => {
    if (metadata) startSync(metadata);
  };
  const [selectedRow, setSelectedRow] = useState<DatasetDetailRow | null>(null);
  const [deleteRowIds, setDeleteRowIds] = useState<string[] | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<RowSelectionState>({});

  const {
    isParsingActive,
    setIsParsingActive,
    setOperationId,
    setIsKmlUpload
  } = useParsingStatus({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataset-data", id] });
      queryClient.invalidateQueries({ queryKey: ["dataset-metadata", id] });
      setRefetchStream(true);
      setOpenUploadDialog(null);
    }
  });

  const { data: configurationList } = useGetConfigurationList({
    dataset_id_filter: id
  });
  // ADM Area Mapping only exists for datasets the organization uploaded, and it
  // is reached from the reworked Data Settings hub.
  // TODO(next task): the onboarding tour still spotlights the old popup layout,
  // so the tour keeps the old one until the tour steps are reworked.
  const isDataSettingsHubEnabled =
    appFeatures.isAdmAreaForUserDataEnabled &&
    !isBringYourOwnDataOnboarding &&
    metadata?.source === "user";

  const { mutate: mutateDelete } = useMutationDeleteDataset();
  const {
    dataSettingType,
    attributesLatitude,
    attributesLongitude,
    dataSettingTemp,
    attributesForAdminArea,
    spatialAttributeLabel,
    isCoordinateRejected,
    hasAdmAreaMapping,
    nullAmount,
    highlightGeoColumns,
    isHighlightNewGeometry,
    isTranslating,
    isSavingDataSetting,
    dataSettingSavedCount,
    isSavingCountry,
    setDataSettingTemp,
    handleSaveCountry,
    handleSaveDataSetting,
    handlePublishDataset,
    handleUnpublishDataset,
    handleRemoveNulls,
    handleHighlightInvalidatedGeometry,
    handleHighlightAdmAreaMapping,
    publishValidation,
    handlePublishButtonClick
  } = useDatasetDataSetting({
    setIsNullConfirmationOpen,
    setOpen,
    id,
    metadata,
    data,
    dataSetting,
    setDataSetting,
    onCloseSettingPopup: () => {
      if (isDataSettingsHubEnabled) return;
      setShowDataSettingPopup(false);
    },
    setRefetchStream,
    isDataSettingsHubEnabled
  });

  const { mutate: mutateDatasetMetadata } = useMutationPatchDatasetMetadata({
    onMutate: () => {
      showToast({
        type: EToastType.LOADING,
        message: "Saving your dataset..."
      });
    },
    onSuccess: () => {
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title="Changes Saved Successfully"
            description="Your changes have been saved, but not published yet."
          />
        )
      });
      setRefetchStream(true);
    },
    onError: (error) => {
      showToast({
        type: EToastType.ERROR,
        message: error.message?.message || "Failed to save dataset."
      });
    }
  });

  const { mutate: putRowDataset } = useUpdateRowsDataset(id || "", {
    onSuccess: (data) => {
      const newData = convertDatasetDataToTableData(data);
      const rowId = newData?.[0]?.[ROW_ID_DETAIL_DATASET];

      dispatch(
        setShowPanelDatasetDetail({ isAddNewRow: false, isEditRow: false })
      );
      removeFormSettingDataset();
      dispatch(clearDrawPolygonDataset());

      setRefetchStream(true);
      // set highlight for edited row
      setHighlightedRow({
        rowId: rowId as string,
        isHighlight: true,
        mode: "edit"
      });
      // fadeout highlight after 7 seconds
      setTimeout(() => {
        setHighlightedRow(null);
      }, 7000);
      showToast({
        id: "edit-row-dataset-success",
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            containerClassName="w-80"
            title="Row Edited"
            description="You have updated a row"
          />
        )
      });
    },
    onError: (error) => {
      setHighlightedRow(null);
      showToast({
        id: "edit-row-dataset-error",
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            description={
              typeof error.message === "object" &&
              error.message !== null &&
              "message" in error.message
                ? capitalizeFirst(
                    (error.message as { message: string }).message
                  )
                : ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION
            }
          />
        )
      });
    }
  });

  const { mutate: mutateDeleteRows } = useDeleteRowsDataset(id || "", {
    onSuccess: () => {
      setRefetchStream(true);
      setDeleteRowIds(null);
      setSelectedRowIds({});
      showToast({
        id: "delete-rows-dataset-success",
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title="Row Deleted Successfully"
            description="The selected row(s) have been removed from the table."
          />
        ),
        className: "!bg-green-50 !border-shamrock-green"
      });
    },
    onMutate: () => {
      showToast({
        id: "delete-rows-dataset-loading",
        type: EToastType.LOADING,
        message: (
          <NotificationContent description="Please wait while row(s) is being deleted." />
        )
      });
    },
    onError: (error) => {
      showToast({
        id: "delete-rows-dataset-error",
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            description={
              typeof error.message === "object" &&
              error.message !== null &&
              "message" in error.message
                ? capitalizeFirst(
                    (error.message as { message: string }).message
                  )
                : ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION
            }
          />
        )
      });
    }
  });

  const { mutate: postAddNewRowDataset } = useAddRowsDataset(id || "", {
    onSuccess: (data) => {
      const newData = convertDatasetDataToTableData(data);
      const rowId = newData?.[0]?.[ROW_ID_DETAIL_DATASET];

      dispatch(
        setShowPanelDatasetDetail({ isAddNewRow: false, isEditRow: false })
      );

      setRefetchStream(true);
      // set highlight for added row
      setHighlightedRow({
        rowId: rowId as string,
        isHighlight: true,
        mode: "add"
      });
      // fadeout highlight after 7 seconds
      setTimeout(() => {
        setHighlightedRow({
          rowId: rowId as string,
          isHighlight: false,
          mode: "add"
        });
      }, 7000);
      removeFormSettingDataset();
      dispatch(clearDrawPolygonDataset());
      showToast({
        id: "add-rows-dataset-success",
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            containerClassName="w-80"
            title="New row added"
            description="You have added a new row"
          />
        )
      });
    },
    onError: (error) => {
      setHighlightedRow(null);
      showToast({
        id: "add-rows-dataset-error",
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            description={
              typeof error.message === "object" &&
              error.message !== null &&
              "message" in error.message
                ? capitalizeFirst(
                    (error.message as { message: string }).message
                  )
                : ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION
            }
          />
        )
      });
    }
  });

  const onClickPreview = async () => {
    if (!id || !datasetData) return;

    // Same guard as the add-dataset flow: coordinates from a projected CRS
    // (e.g. UTM Zone 48S / EPSG:32748) are meters and would render nowhere on
    // the WGS84 preview map, so block the preview with an explanatory toast.
    // Mirror the schema filtering used by handleAddSpatialAnalysisDataset so
    // the same geometry columns are validated.
    const previewSchema = metadata?.schema?.filter(
      (attr) => attr.is_visible && attr.attribute_name !== ROW_ID_DETAIL_DATASET
    );
    if (
      !isFeatureCollectionWGS84(
        convertDatasetResponseToFeatureCollection(datasetData, previewSchema)
      )
    ) {
      showToast({
        id: "preview-dataset-unsupported-crs",
        type: EToastType.ERROR,
        message: (
          <NotificationContent title={MESSAGE.DATASET_ADD_TITLE_FAILED}>
            <p className="mt-1">
              {MESSAGE.DATASET_ADD_DESCRIPTION_PREFIX}
              <span className="mx-1 font-semibold">{metadata?.name}</span>
              {MESSAGE.DATASET_ADD_DESCRIPTION_UNSUPPORTED_CRS}
            </p>
          </NotificationContent>
        )
      });
      return;
    }

    dispatch(
      setDatasetHistory({
        rawDatasets: rawDatasets,
        datasets: datasets,
        visualizationOrder: visualizationOrder
      })
    );
    dispatch(
      setWidgetHistory({
        widgets,
        visualizationOrder: widgetVisualizationOrder,
        widgetIndicates
      })
    );
    dispatch(resetUIDataConfig());

    handleClearDataset();

    navigate(`/?preview=true&id=${id}`, {
      state: { datasetData, datasetMetadataSchema: metadata?.schema }
    });
  };

  // Prevent close dialog when uploading
  const handleOpenUploadDialog = () => {
    if (openUploadDialog && !isUploading) setOpenUploadDialog(null);
  };

  const handleDeleteDataset = async () => {
    if (metadata?.published) handleCheckUnpublish();
    else {
      const analysisUsageText =
        !configurationList?.data.length || configurationList?.data.length <= 1
          ? "analysis"
          : "analyses";

      showConfirmation({
        title: "Delete This Dataset?",
        message: (
          <div className="text-center px-3 text-sm">
            <p>
              This step cannot be undone.{" "}
              <span className="font-bold">
                {`${configurationList?.data.length} ${analysisUsageText}`}
              </span>{" "}
              currently use this dataset and will be impacted.
            </p>
          </div>
        ),
        cancelLabel: "",
        confirmLabel: "Delete Dataset",
        onConfirm: () => {
          if (!id) {
            showToast({
              id: "download-dataset-error",
              type: EToastType.ERROR,
              message: "Dataset id not found."
            });

            return;
          }
          mutateDelete(id);
        }
      });
    }
  };

  const handleAddMoreDataset = () => {
    if (metadata?.published) handleCheckUnpublish();
    else setOpenUploadDialog(EUploadDatasetType.ADD_MORE);
  };

  const handleReplaceDataset = () => {
    if (metadata?.published) handleCheckUnpublish();
    else setOpenUploadDialog(EUploadDatasetType.REPLACE_ALL);
  };

  const handleCheckUnpublish = () => {
    showConfirmation({
      title: "Unpublish Dataset?",
      message:
        MESSAGE.DATASET_MANAGEMENT_UNPUBLISH_DATASET_CONFIRMATION_DESCRIPTION,
      cancelLabel: "",
      confirmLabel: "Unpublish",
      onConfirm: () => handleUnpublishDataset()
    });
  };

  const handleShowDataSettingPopup = () => {
    // While the tour runs it fully owns the popup via the active step. Ignore
    // Radix's own open/close requests so no stale open flag leaks out when the
    // tour ends (skip/finish) and re-surfaces the popup.
    if (isBringYourOwnDataOnboarding) return;
    if (showDataSettingPopup) {
      setDataSettingTemp(null);
      setShowDataSettingPopup(false);
    } else {
      setDataSettingTemp(dataSetting);
      setShowDataSettingPopup(true);
    }
  };

  const handleSaveDatasetMetadataInfo = (data: DatasetBasicInfoForm) => {
    mutateDatasetMetadata({
      id: id!,
      ...data
    });
  };

  const handleCloseRemoveNullsConfirmation = (open: boolean) => {
    setIsNullConfirmationOpen(open);
    handleHighlightInvalidatedGeometry();
  };

  const onSubmit = (data: Record<string, unknown>) => {
    const adm_area_source_attribute_name = data["admin_area_level"] || null;
    const result = formatAddAndUpdateDatasetRow(data, metadata);
    postAddNewRowDataset({
      data: result,
      adm_area_source: adm_area_source_attribute_name as string | null
    });
  };

  const handleAddNewRowPanel = () => {
    if (metadata?.published) {
      handleCheckUnpublish();
    } else {
      dispatch(
        setShowPanelDatasetDetail({
          isAddNewRow: true,
          isEditRow: false
        })
      );
    }
  };

  const onEdit = (
    data: Record<string, unknown>,
    originalData?: DatasetDetailRow | null
  ) => {
    const adm_area_source_attribute_name = data["admin_area_level"] || null;
    const result = formatAddAndUpdateDatasetRow(data, metadata);

    if (originalData) {
      putRowDataset({
        rowId:
          (selectedRow?.[ROW_ID_DETAIL_DATASET] as string) ||
          (originalData[ROW_ID_DETAIL_DATASET] as string),
        data: result,
        adm_area_source: adm_area_source_attribute_name as string | null
      });
    } else {
      showToast({
        id: "edit-row-dataset-error",
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
            description="Reference ID not found for the row."
          />
        )
      });
    }
  };

  const handleEditRowPanel = (row: DatasetDetailRow) => {
    setSelectedRow(row);
    dispatch(
      setShowPanelDatasetDetail({
        isAddNewRow: false,
        isEditRow: true
      })
    );
  };

  const handleRowSelectionChange = (updater: unknown) => {
    setSelectedRowIds((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;

      const selectedIds = Object.keys(next).filter((key) => next[key]);
      setDeleteRowIds(selectedIds);

      return next;
    });
  };

  const handleDeleteRows = (id?: string) => {
    showConfirmation({
      title: deleteRowIds?.length
        ? `Delete ${deleteRowIds.length} Selected Rows?`
        : MESSAGE.DELETE_DATASET_MANAGEMENT_ROWS_CONFIRMATION_TITLE,
      message: (
        <div className="text-center">
          {MESSAGE.DELETE_DATASET_MANAGEMENT_ROWS_CONFIRMATION_DESCRIPTION}
        </div>
      ),
      cancelLabel: "Cancel",
      confirmLabel: "Yes, delete",
      onConfirm: () =>
        mutateDeleteRows(deleteRowIds?.length ? deleteRowIds : [id!])
    });
  };

  // Show parsing state if active
  if (isParsingActive) {
    return <LoadingState message="Loading..." testId="parsing-state" />;
  }

  const canPreview =
    !isLoading &&
    !!metadata?.country_id &&
    !!metadata?.spatial_aggregation_type;

  // During onboarding the popup is driven purely by the active step. Outside
  // onboarding it follows the user-driven state. Not derived from
  // showDataSettingPopup during the tour, so exiting mid-step can't leave a
  // stale open flag behind.
  const isDataSettingPopupOpen = isBringYourOwnDataOnboarding
    ? dataSettingPopupTour.isPopupOpen
    : showDataSettingPopup;

  return (
    <div className="flex flex-col h-full overflow-auto">
      {isTranslating && !isDataSettingsHubEnabled && (
        <output
          className="absolute top-0 left-0 w-full h-full bg-black/50 z-[52] flex items-center justify-center"
          aria-live="polite"
          aria-label="Loading"
        >
          <div
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center"
            aria-hidden="true"
          >
            <LoaderIcon className="animate-spin" width={20} height={20} />
          </div>
          <span className="sr-only">Loading...</span>
        </output>
      )}
      <div className="flex flex-row items-center px-6 pt-6 pb-2">
        <Breadcrumb>
          <BreadcrumbList className="!gap-1 items-center">
            <BreadcrumbItem>
              <BreadcrumbLink
                data-testid="breadcrumb-back-link"
                href={withBasePath("dataset-management")}
              >
                Dataset Management
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <ArrowRightIcon fill="#CECECE" width={12} height={12} />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Dataset Details</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div
          data-testid="dataset-status"
          className={cn(
            "px-2 py-1 text-xs font-semibold rounded-lg ml-auto",
            metadata?.published === false
              ? "bg-neutral-100 border border-neutral-200 text-txtBody"
              : "text-green-700 font-semibold text-xs border-green-200 bg-green-50"
          )}
        >
          {metadata?.published
            ? DatasetPublishStatus.PUBLISHED
            : DatasetPublishStatus.UNPUBLISHED}
        </div>
      </div>

      <div className="flex flex-col p-6 pt-0 h-full">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <ArrowLeftIcon
              fill="#CECECE"
              width={32}
              height={32}
              className="cursor-pointer"
              onClick={() => navigate("/dataset-management")}
            />
            <EditField
              inputFieldName={`${TEST_ID_PREFIX}-dataset-name`}
              value={metadata?.name ?? ""}
              placeHolder="Dataset Name"
              textClassName="text-2xl text-txtHeading font-semibold"
              iconClassName="w-8 h-8 fill-teal-500"
              testId={`${TEST_ID_PREFIX}-dataset-name`}
              disabled={
                isLoading ||
                (!isEditDataGranted && !isEditBlocked) ||
                metadata?.published
              }
              onEditBlocked={
                isEditBlocked ? showPermissionDeniedToast : undefined
              }
              onClickCheckIcon={(value) =>
                handleSaveDatasetMetadataInfo({ name: value })
              }
            />
          </div>
          <div className="flex flex-col my-2">
            <EditField
              inputFieldName={`${TEST_ID_PREFIX}-dataset-description`}
              value={metadata?.short_description ?? ""}
              valuePlaceholder="No description available."
              placeHolder="Short Description"
              textClassName="text-txtBody text-xs"
              className="-px-4"
              iconClassName="fill-teal-500"
              testId={`${TEST_ID_PREFIX}-dataset-description`}
              disabled={
                isLoading ||
                (!isEditDataGranted && !isEditBlocked) ||
                metadata?.published
              }
              onEditBlocked={
                isEditBlocked ? showPermissionDeniedToast : undefined
              }
              onClickCheckIcon={(value) =>
                handleSaveDatasetMetadataInfo({
                  short_description: value
                })
              }
            />
            <div className="text-cinnabar text-xs pt-0.5">
              {publishValidation.errors.description}
            </div>
          </div>
        </div>
        <div className="mb-4">
          <DatasetConfigurationSection
            metadata={metadata}
            isLoading={isLoading}
            isSyncing={isSyncing}
            handleSyncDataset={handleSyncDataset}
            isDatasetPublished={metadata?.published || false}
            handleAddMoreDataset={handleAddMoreDataset}
            handleReplaceDataset={handleReplaceDataset}
            handleDeleteDataset={handleDeleteDataset}
            handleAddNewRowDataset={handleAddNewRowPanel}
            setshowDataSettingPopup={setShowDataSettingPopup}
            handleDeleteRows={handleDeleteRows}
            deleteRowCount={deleteRowIds?.length || 0}
            resetDeleteRow={() => {
              setDeleteRowIds(null);
              setSelectedRowIds({});
            }}
          />
          <div className="text-cinnabar text-xs pt-0.5">
            {publishValidation.errors.dataSettings}
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : (
          <div data-tourid="bring-your-own-data-detail-table">
            <TableContainer
              data={data}
              metadata={metadata}
              dynamicColumns={dynamicColumns({
                handleEditRow: guardPermission(
                  isEditDataGranted,
                  handleEditRowPanel
                ),
                handleDeleteRows: guardPermission(
                  isDeleteDataGranted,
                  handleDeleteRows
                ),
                isRowDatasetManagementEnabled,
                isEditDataGranted,
                isDeleteDataGranted,
                isGranularRbacEnabled
              })}
              isSyncing={isSyncing}
              highlightGeoColumns={highlightGeoColumns}
              isHighlightNewGeometry={isHighlightNewGeometry}
              publishValidation={publishValidation}
              rowSelection={selectedRowIds}
              setRowSelection={handleRowSelectionChange}
              isHighlightedRow={{
                rowId: highlightedRow?.rowId,
                isHighlight: highlightedRow?.isHighlight
              }}
            />
          </div>
        )}
        <div className="flex flex-row items-center gap-2 ml-auto mt-4">
          <Button
            data-testid="preview-button"
            variant="outline"
            className="py-2.5 h-auto w-[200px] rounded-xl font-semibold text-sm"
            onClick={onClickPreview}
            disabled={isLoading || !canPreview}
          >
            Preview in Map
          </Button>
          {(isEditDataGranted || isGranularRbacEnabled) && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="publish-button"
                  data-tourid={BRING_YOUR_OWN_DATA_PUBLISH_DATA_TOUR_ID}
                  variant="default"
                  className="py-2.5 h-auto w-[200px] rounded-xl font-semibold text-sm"
                  disabled={isLoading || isSyncing}
                  onClick={(event) => {
                    if (!isEditDataGranted) showPermissionDeniedToast();
                    const isAllowed =
                      isEditDataGranted && handlePublishButtonClick();
                    if (!isAllowed) {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                  aria-label={
                    metadata?.published
                      ? "Unpublish dataset"
                      : "Publish dataset"
                  }
                >
                  {metadata?.published ? "Unpublish" : "Publish"}
                </Button>
              </DialogTrigger>
              <ConfirmationDialog
                metadata={metadata}
                setOpen={setOpen}
                handlePublishDataset={handlePublishDataset}
                handleUnpublishDataset={handleUnpublishDataset}
              />
            </Dialog>
          )}
        </div>

        {/* Upload Dialog */}
        <Dialog
          open={openUploadDialog !== null}
          onOpenChange={handleOpenUploadDialog}
        >
          {openUploadDialog && (
            <UploadDialog
              uploadType={openUploadDialog}
              setOperationId={setOperationId}
              setIsParsingActive={setIsParsingActive}
              setIsKmlUpload={setIsKmlUpload}
              setIsLoading={setIsUploading}
              datasetId={id}
            />
          )}
        </Dialog>

        {/* Data Setting Popup */}
        {isDataSettingPopupOpen && (
          <Dialog
            open
            onOpenChange={handleShowDataSettingPopup}
            // During the onboarding tour the popup must be non-modal. A modal
            // Radix dialog disables outside pointer events (body
            // pointer-events: none), which kills the tour tooltip's buttons —
            // they render in a body portal outside the dialog. Normal usage
            // stays fully modal.
            modal={!isBringYourOwnDataOnboarding}
          >
            {id && dataSettingType && isDataSettingsHubEnabled && (
              <DataSettingsDialog
                datasetId={id}
                countryId={metadata?.country_id}
                type={dataSettingType}
                dataSetting={dataSettingTemp}
                isSavingDataSetting={isSavingDataSetting}
                dataSettingSavedCount={dataSettingSavedCount}
                isBehindAnotherDialog={isNullConfirmationOpen}
                hasSavedAdmAreaMapping={hasAdmAreaMapping}
                onMappingSaved={() => {
                  setRefetchStream(true);
                  handleHighlightAdmAreaMapping();
                }}
                schema={metadata?.schema ?? EMPTY_SCHEMA}
                // The detail page reads the dataset over its own stream.
                onTimeSeriesSaved={() => setRefetchStream(true)}
                setDataSetting={setDataSettingTemp}
                onClose={() => setShowDataSettingPopup(false)}
                onSaveDataSetting={handleSaveDataSetting}
                isSavingCountry={isSavingCountry}
                onSaveCountry={handleSaveCountry}
                attributesLatitude={attributesLatitude}
                attributesLongitude={attributesLongitude}
                attributesForAdminArea={attributesForAdminArea}
                spatialAttributeLabel={spatialAttributeLabel}
                isCoordinateRejected={isCoordinateRejected}
              />
            )}
            {dataSettingType && !isDataSettingsHubEnabled && (
              <DataSettingPopup
                // Suppressed during the tour: the auto-select effect would
                // pick the country on both popup steps, but only the
                // spatial-aggregation step shows it (via onboardingDataSetting).
                countryId={
                  isBringYourOwnDataOnboarding
                    ? undefined
                    : metadata?.country_id
                }
                type={dataSettingType}
                dataSetting={
                  isBringYourOwnDataOnboarding
                    ? dataSettingPopupTour.dataSetting
                    : dataSettingTemp
                }
                tourId={BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID}
                spatialAggregationTourId={
                  dataSettingPopupTour.spatialAggregationTourId
                }
                geometryAttributeOptions={
                  dataSettingPopupTour.geometryAttributeOptions
                }
                disableAnimation={isBringYourOwnDataOnboarding}
                setDataSetting={setDataSettingTemp}
                onClose={() => setShowDataSettingPopup(false)}
                onSaveDataSetting={handleSaveDataSetting}
                attributesLatitude={attributesLatitude}
                attributesLongitude={attributesLongitude}
                attributesForAdminArea={attributesForAdminArea}
              />
            )}
          </Dialog>
        )}
      </div>

      <Dialog
        open={isNullConfirmationOpen}
        onOpenChange={handleCloseRemoveNullsConfirmation}
      >
        <ConfirmationNullDialog
          nullAmount={nullAmount}
          setIsNullConfirmationOpen={setIsNullConfirmationOpen}
          handleRemoveNulls={handleRemoveNulls}
          handleHighlightInvalidatedGeometry={
            handleHighlightInvalidatedGeometry
          }
        />
      </Dialog>
      {isRowDatasetManagementEnabled && (
        <ActionPanel
          schema={metadata?.schema || []}
          spatialAggregationType={metadata?.spatial_aggregation_type || null}
          selectedRow={selectedRow}
          onSubmit={onSubmit}
          onEdit={onEdit}
        />
      )}
    </div>
  );
}
